import { seoTableApi } from '@/api'
import { supportedLocales } from '@/i18n'

const STORAGE_KEY = 'seo:v1';

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function safeString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normalizeRecord(record) {
  const r = record || {};
  return {
    title: safeString(r.title),
    keywords: safeString(r.keywords),
    description: safeString(r.description),
    robots: safeString(r.robots),
    canonical: safeString(r.canonical),
    ogTitle: safeString(r.ogTitle),
    ogDescription: safeString(r.ogDescription),
  };
}

function normalizeState(raw) {
  const byPath = {};
  const source = (raw && raw.byPath) || {};
  Object.keys(source).forEach((path) => {
    const byLocale = source[path] || {};
    byPath[path] = {};
    Object.keys(byLocale).forEach((locale) => {
      byPath[path][locale] = normalizeRecord(byLocale[locale]);
    });
  });
  return { byPath };
}

function safePath(value) {
  const str = safeString(value)
  if (!str) return ''
  if (str === '*') return '*'
  if (str[0] !== '/') return `/${str}`
  return str
}

function pathMatchesPattern(path, pattern) {
  const p = safePath(path)
  const pat = safePath(pattern)
  if (!p || !pat) return false
  if (p === pat) return true
  if (!pat.includes(':')) return false
  const pSeg = p.split('/').filter(Boolean)
  const patSeg = pat.split('/').filter(Boolean)
  if (pSeg.length !== patSeg.length) return false
  for (let i = 0; i < patSeg.length; i += 1) {
    const seg = patSeg[i]
    if (seg[0] === ':') continue
    if (seg !== pSeg[i]) return false
  }
  return true
}

function normalizeSeoTableRow(row) {
  const r = row || {}
  return {
    title: safeString(r.title),
    keywords: safeString(r.keyword),
    description: safeString(r.describe),
  }
}

export default {
  namespaced: true,
  state: () => ({
    byPath: {},
  }),
  getters: {
    getSeoByPathAndLocale: (state) => (path, locale) => {
      const exact = state.byPath[path] || {};
      if (exact[locale]) return exact[locale] || {};

      const keys = Object.keys(state.byPath)
      for (let i = 0; i < keys.length; i += 1) {
        const pattern = keys[i]
        if (!pattern || !pattern.includes(':')) continue
        if (!pathMatchesPattern(path, pattern)) continue
        const byLocale = state.byPath[pattern] || {}
        if (byLocale[locale]) return byLocale[locale] || {}
      }

      return {};
    },
    getLocalesForPath: (state) => (path) => {
      const byLocale = state.byPath[path] || {};
      return Object.keys(byLocale);
    },
  },
  mutations: {
    replaceState(state, payload) {
      state.byPath = (payload && payload.byPath) || {};
    },
    setSeo(state, { path, locale, record }) {
      const normalized = normalizeRecord(record);
      const prevByLocale = state.byPath[path] || {};
      state.byPath = {
        ...state.byPath,
        [path]: {
          ...prevByLocale,
          [locale]: normalized,
        },
      };
    },
  },
  actions: {
    init({ commit, dispatch }) {
      const raw = safeParse(localStorage.getItem(STORAGE_KEY));
      commit('replaceState', normalizeState(raw));
      dispatch('loadFromServer').catch(() => undefined)
    },
    setSeoForPath({ commit }, { path, locale, record }) {
      commit('setSeo', { path, locale, record });
    },
    async loadFromServer({ commit }) {
      if (typeof fetch !== 'function') return

      const res = await seoTableApi.selectAll()
      const rows = res && res.code === 200 && Array.isArray(res.data) ? res.data : []
      if (!rows.length) return

      const locales = Array.isArray(supportedLocales) && supportedLocales.length
        ? supportedLocales.map((l) => l && l.code).filter(Boolean)
        : []

      rows.forEach((row) => {
        const path = safePath(row && row.path)
        if (!path) return
        const record = normalizeSeoTableRow(row)
        if (!locales.length) {
          commit('setSeo', { path, locale: 'zh-CN', record })
          return
        }
        locales.forEach((locale) => {
          commit('setSeo', { path, locale, record })
        })
      })
    },
  },
};

export function seoPersistencePlugin(store) {
  store.subscribe((mutation, state) => {
    if (!mutation.type.startsWith('seo/')) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ byPath: state.seo.byPath })
    );
  });
}
